import React, { useRef, useState, useCallback, useEffect } from 'react'
import { Cropper } from 'react-advanced-cropper'
import 'react-advanced-cropper/dist/style.css'
import { Button, Tooltip, Slider } from '@mui/material'
import Loaders from '@/product/components/molecules/Loaders'
import { useTranslation } from 'react-i18next'

export default function ImageCropper(props) {
    const cropperRef = useRef(null)
    const prevZoomRef = useRef(1)
    const containerRef = useRef(null)
    const footerRef = useRef(null)
    const { t } = useTranslation()

    const [containerH, setContainerH] = useState(0)
    const [footerH, setFooterH] = useState(90)
    const [rotation, setRotation] = useState(0)
    const [zoom, setZoom] = useState(1)
    const [isWorking, setIsWorking] = useState(false)
    const [imgKey, setImgKey] = useState(() =>
        props.imgSrc
            ? `${props.imgSrc}-${Math.random().toString(36).slice(2, 8)}`
            : null
    )

    // 🚫 Prevent ESC from closing popup
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation()
                e.preventDefault()
            }
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [])

    // 📏 Measure container and footer
    useEffect(() => {
        const measure = () => {
            if (containerRef.current)
                setContainerH(containerRef.current.offsetHeight)
            if (footerRef.current) setFooterH(footerRef.current.offsetHeight)
        }
        measure()
        const ro = new ResizeObserver(measure)
        if (containerRef.current) ro.observe(containerRef.current)
        if (footerRef.current) ro.observe(footerRef.current)
        window.addEventListener('resize', measure)
        return () => {
            ro.disconnect()
            window.removeEventListener('resize', measure)
        }
    }, [])

    // ♻️ Reset state when new image loads
    useEffect(() => {
        setRotation(0)
        setZoom(1)
        prevZoomRef.current = 1
        setImgKey(
            props.imgSrc
                ? `${props.imgSrc}-${Math.random().toString(36).slice(2, 8)}`
                : null
        )
    }, [props.imgSrc])

    // 🧩 Set default crop to full visible area
    const setFullCropArea = useCallback(() => {
        const cropper = cropperRef.current
        if (!cropper) return

        // Small delay to ensure internal state is ready if called rapidly
        requestAnimationFrame(() => {
            const visible = cropper.getVisibleArea?.()
            const img = cropper.getImage?.()
            if (visible && visible.width && visible.height) {
                cropper.setCoordinates({
                    left: visible.left ?? 0,
                    top: visible.top ?? 0,
                    width: visible.width,
                    height: visible.height,
                })
            } else if (img && img.width && img.height) {
                cropper.setCoordinates({
                    left: 0,
                    top: 0,
                    width: img.width,
                    height: img.height,
                })
            }
        })
    }, [])

    const handleCropperReady = useCallback(() => {
        setFullCropArea()
    }, [setFullCropArea])

    // 🔄 Rotate & Flip with auto-fit zoom
    const fitImageToView = useCallback(() => {
        const cropper = cropperRef.current
        if (!cropper) return

        // Use requestAnimationFrame instead of setTimeout for smoother UI updates
        requestAnimationFrame(() => {
            // Reset zoom to fit the rotated image
            const state = cropper.getState?.()
            const visibleArea = cropper.getVisibleArea?.()

            if (state && visibleArea) {
                // Calculate zoom to fit image in visible area
                const imageAspect =
                    state.imageSize.width / state.imageSize.height
                const visibleAspect = visibleArea.width / visibleArea.height

                let newZoom = 1
                if (imageAspect > visibleAspect) {
                    // Image is wider - fit to width
                    newZoom = visibleArea.width / state.imageSize.width
                } else {
                    // Image is taller - fit to height
                    newZoom = visibleArea.height / state.imageSize.height
                }

                // Apply zoom with some padding (0.9 to leave 10% margin)
                const fitZoom = Math.max(0.1, newZoom * 0.9)
                cropper.zoomImage(fitZoom / (prevZoomRef.current || 1))
                prevZoomRef.current = fitZoom
                setZoom(fitZoom)
            }

            setFullCropArea()
        })
    }, [setFullCropArea])

    const handleRotateCW = useCallback(() => {
        cropperRef.current?.rotateImage(90)
        setRotation((p) => (p + 90) % 360)
        fitImageToView()
    }, [fitImageToView])

    const handleRotateCCW = useCallback(() => {
        cropperRef.current?.rotateImage(-90)
        setRotation((p) => (p - 90 + 360) % 360)
        fitImageToView()
    }, [fitImageToView])

    const handleFlipHorizontal = useCallback(() => {
        cropperRef.current?.flipImage(true, false)
        setFullCropArea()
    }, [setFullCropArea])

    const handleFlipVertical = useCallback(() => {
        cropperRef.current?.flipImage(false, true)
        setFullCropArea()
    }, [setFullCropArea])

    // Helper functions for logarithmic zoom scale
    // Slider 0 = 1x zoom
    // Slider 50 = 4x zoom (2^2)
    // Slider -50 = 0.25x zoom (2^-2)
    const zoomToSlider = (zoomVal) => {
        return 25 * Math.log2(zoomVal)
    }

    const sliderToZoom = (sliderVal) => {
        return Math.pow(2, sliderVal / 25)
    }

    const handleZoomChange = useCallback((_, val) => {
        const cropper = cropperRef.current
        if (!cropper) return
        const sliderVal = Array.isArray(val) ? val[0] : val

        // Convert slider value to zoom scale
        const newZoom = sliderToZoom(sliderVal)

        const prev = prevZoomRef.current || 1
        const delta = newZoom / prev
        cropper.zoomImage(delta)
        prevZoomRef.current = newZoom
        setZoom(newZoom)
    }, [])

    // 💾 Save cropped image
    const onCropSave = useCallback(() => {
        if (!props.imgSrc) return
        try {
            setIsWorking(true)
            const canvas = cropperRef.current?.getCanvas()
            if (!canvas) {
                setIsWorking(false)
                return
            }

            // Detect mime type from source or default to jpeg
            let mimeType = 'image/jpeg'
            if (typeof props.imgSrc === 'string') {
                const match = props.imgSrc.match(
                    /^data:(image\/[a-zA-Z+]+);base64,/
                )
                if (match && match[1]) {
                    mimeType = match[1]
                } else if (props.imgSrc.toLowerCase().endsWith('.png')) {
                    mimeType = 'image/png'
                } else if (props.imgSrc.toLowerCase().endsWith('.webp')) {
                    mimeType = 'image/webp'
                }
            }

            // Use detected mime type. For JPEG/WEBP, use quality 0.92. For PNG, quality is ignored.
            const dataUrl = canvas.toDataURL(mimeType, 0.92)

            props.getCroppedImage?.(dataUrl)
            props.onClose?.()
            setIsWorking(false)
        } catch (err) {
            console.error('❌ onCropSave failed:', err)
            setIsWorking(false)
        }
    }, [props])

    const loading = props.loading || !props.imgSrc

    // 🧠 Smart crop height
    const isMobile = window.innerWidth < 768
    // Increase max available height for desktop
    const maxAvailable = containerH - footerH - 20

    // Increased minimum height for desktop from 300 to 500
    // Increased percentage for desktop from 0.8 to 0.85
    const cropHeight = Math.max(
        isMobile ? 300 : 500,
        isMobile ? maxAvailable * 0.7 : maxAvailable * 0.85
    )

    return (
        <>
            {loading ? (
                <div className="noGrid cropLoader">
                    <Loaders isLoading={true} />
                </div>
            ) : (
                <div
                    ref={containerRef}
                    className="imageCropCnt"
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        width: '100%',
                        height: '100%',
                        maxHeight: '90vh',
                        background: 'transparent',
                        overflow: 'hidden',
                        position: 'relative',
                        borderRadius: '8px',
                    }}
                >
                    {/* 🖼️ Crop Zone */}
                    <div
                        style={{
                            width: '100%',
                            height: `${cropHeight}px`,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            background: 'transparent',
                            overflow: 'hidden',
                            flexShrink: 0,
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                        }}
                    >
                        <Cropper
                            key={imgKey}
                            ref={cropperRef}
                            src={props.imgSrc}
                            onReady={handleCropperReady}
                            stencilProps={{
                                movable: true,
                                resizable: true,
                            }}
                            imageRestriction="stencil"
                            className="advanced-cropper"
                            style={{
                                width: '100%',
                                height: '100%',
                                background: 'transparent',
                            }}
                        />
                        {isWorking && (
                            <div
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'grid',
                                    placeItems: 'center',
                                    background: 'rgba(0,0,0,0.35)',
                                }}
                            >
                                <Loaders isLoading={true} />
                            </div>
                        )}
                    </div>

                    {/* 🎛️ Toolbar */}
                    <div
                        ref={footerRef}
                        className="cropActions"
                        style={{
                            width: '100%',
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 16,
                            padding: '14px 16px',
                            background: 'rgba(0, 0, 0, 0.6)',
                            backdropFilter: 'blur(8px)',
                            borderTopLeftRadius: 10,
                            borderTopRightRadius: 10,
                            boxShadow: '0 -2px 8px rgba(0,0,0,0.25)',
                            flexShrink: 0,
                        }}
                    >
                        <Tooltip title="Rotate 90° counter-clock-wise">
                            <Button
                                className="iconBtn"
                                onClick={handleRotateCCW}
                                disabled={isWorking}
                            >
                                <span className="material-icons">
                                    rotate_90_degrees_ccw
                                </span>
                            </Button>
                        </Tooltip>

                        <Tooltip title="Rotate 90° clock-wise">
                            <Button
                                className="iconBtn"
                                onClick={handleRotateCW}
                                disabled={isWorking}
                            >
                                <span className="material-icons">
                                    rotate_90_degrees_cw
                                </span>
                            </Button>
                        </Tooltip>

                        <Tooltip title="Flip Horizontal">
                            <Button
                                className="iconBtn"
                                onClick={handleFlipHorizontal}
                                disabled={isWorking}
                            >
                                <span className="material-icons">flip</span>
                            </Button>
                        </Tooltip>

                        <Tooltip title="Flip Vertical">
                            <Button
                                className="iconBtn"
                                onClick={handleFlipVertical}
                                disabled={isWorking}
                            >
                                <span className="material-icons">
                                    flip_camera_android
                                </span>
                            </Button>
                        </Tooltip>

                        {/* Zoom Slider */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                minWidth: 160,
                                flexGrow: 1,
                                maxWidth: 250,
                            }}
                        >
                            <span
                                className="material-icons"
                                style={{ fontSize: 20 }}
                            >
                                zoom_out
                            </span>
                            <Slider
                                value={zoomToSlider(zoom)}
                                min={-50}
                                max={50}
                                step={1}
                                onChange={handleZoomChange}
                                disabled={isWorking}
                            />
                            <span
                                className="material-icons"
                                style={{ fontSize: 20 }}
                            >
                                zoom_in
                            </span>
                        </div>

                        {/* Save Button */}
                        <Button
                            className="saveBtn"
                            onClick={onCropSave}
                            disabled={isWorking}
                        >
                            {isWorking ? t('Saving...') : t('Save Selection')}
                        </Button>
                    </div>
                </div>
            )}
        </>
    )
}
